import PopoverPosition from '../helpers/popover-position.js'

// Vue directive for Bootstrap-compatible tooltips without jQuery
// Uses same HTML structure and CSS as Bootstrap 3

const instances = []
let uid = 0

// Hide all tooltips on touch (for mobile)
document.addEventListener('touchstart', e => {
  instances.forEach(instance => {
    if (instance.tipEl && instance.el !== e.target && !instance.el.contains(e.target)) {
      instance.hide()
    }
  })
})

// Hide tooltips on scroll (they can get mispositioned)
window.addEventListener('scroll', () => {
  instances.forEach(instance => {
    if (instance.visible) {
      instance.hide()
    }
  })
}, true)

class Tooltip {
  constructor(el, binding) {
    this.el = el
    this.tipEl = null
    this.visible = false
    this.showTimeout = null
    this.hideTimeout = null
    this.uid = uid++

    this.update(binding)
    this.bindEvents()
  }

  update(binding) {
    this.content = binding.value || ''
    this.placement = binding.arg || 'top'
    this.html = !!binding.modifiers.html

    // Parse triggers from modifiers
    this.triggers = []
    if (binding.modifiers.hover) this.triggers.push('hover')
    if (binding.modifiers.focus) this.triggers.push('focus')
    if (binding.modifiers.click) this.triggers.push('click')
    // Default to hover if no triggers specified
    if (this.triggers.length === 0) this.triggers = ['hover', 'focus']

    // Update content if tooltip is visible
    if (this.tipEl) {
      const inner = this.tipEl.querySelector('.tooltip-inner')
      if (inner) {
        if (this.html) {
          inner.innerHTML = this.content
        } else {
          inner.textContent = this.content
        }
      }
    }
  }

  bindEvents() {
    this.handlers = {
      mouseenter: () => this.scheduleShow(),
      mouseleave: () => this.scheduleHide(),
      focusin: () => this.scheduleShow(),
      focusout: () => this.scheduleHide(),
      click: e => {
        e.preventDefault()
        this.toggle()
      }
    }

    if (this.triggers.includes('hover')) {
      this.el.addEventListener('mouseenter', this.handlers.mouseenter)
      this.el.addEventListener('mouseleave', this.handlers.mouseleave)
    }
    if (this.triggers.includes('focus')) {
      this.el.addEventListener('focusin', this.handlers.focusin)
      this.el.addEventListener('focusout', this.handlers.focusout)
    }
    if (this.triggers.includes('click')) {
      this.el.addEventListener('click', this.handlers.click)
    }
  }

  unbindEvents() {
    this.el.removeEventListener('mouseenter', this.handlers.mouseenter)
    this.el.removeEventListener('mouseleave', this.handlers.mouseleave)
    this.el.removeEventListener('focusin', this.handlers.focusin)
    this.el.removeEventListener('focusout', this.handlers.focusout)
    this.el.removeEventListener('click', this.handlers.click)
  }

  createTipEl() {
    const tip = document.createElement('div')
    tip.className = 'tooltip'
    tip.setAttribute('role', 'tooltip')
    tip.id = `tooltip-${this.uid}`

    const arrow = document.createElement('div')
    arrow.className = 'tooltip-arrow'

    const inner = document.createElement('div')
    inner.className = 'tooltip-inner'
    if (this.html) {
      inner.innerHTML = this.content
    } else {
      inner.textContent = this.content
    }

    tip.appendChild(arrow)
    tip.appendChild(inner)

    return tip
  }

  scheduleShow() {
    clearTimeout(this.hideTimeout)
    this.showTimeout = setTimeout(() => this.show(), 0)
  }

  scheduleHide() {
    clearTimeout(this.showTimeout)
    this.hideTimeout = setTimeout(() => this.hide(), 0)
  }

  show() {
    if (!this.content) return
    if (this.visible) return

    // Create tooltip element if needed
    if (!this.tipEl) {
      this.tipEl = this.createTipEl()
    }

    // Add to body
    document.body.appendChild(this.tipEl)

    // Position it
    const pos = PopoverPosition.position(this.el, this.tipEl, this.placement)

    // Remove old placement classes, add new one
    this.tipEl.className = `tooltip ${pos.placement}`

    // Apply position
    this.tipEl.style.top = `${pos.top}px`
    this.tipEl.style.left = `${pos.left}px`

    // Adjust arrow if needed
    if (pos.arrowOffset !== null) {
      const arrow = this.tipEl.querySelector('.tooltip-arrow')
      if (pos.placement === 'top' || pos.placement === 'bottom') {
        arrow.style.left = `${pos.arrowOffset}%`
      } else {
        arrow.style.top = `${pos.arrowOffset}%`
      }
    }

    // Trigger reflow then add 'in' class for animation
    this.tipEl.offsetHeight // force reflow
    this.tipEl.classList.add('in')

    this.visible = true
    this.el.setAttribute('aria-describedby', this.tipEl.id)
  }

  hide() {
    if (!this.visible) return
    if (!this.tipEl) return

    this.tipEl.classList.remove('in')

    // Remove after transition
    setTimeout(() => {
      this.tipEl?.parentNode?.removeChild(this.tipEl)
    }, 150) // Match Bootstrap's TRANSITION_DURATION

    this.visible = false
    this.el.removeAttribute('aria-describedby')
  }

  toggle() {
    if (this.visible) {
      this.hide()
    } else {
      this.show()
    }
  }

  destroy() {
    clearTimeout(this.showTimeout)
    clearTimeout(this.hideTimeout)
    this.unbindEvents()
    this.tipEl?.parentNode?.removeChild(this.tipEl)
    this.tipEl = null
  }
}

// Vue 3 directive (exported for app.directive() registration)
export default {
  beforeMount(el, binding) {
    const instance = new Tooltip(el, binding)
    el._tooltipInstance = instance
    instances.push(instance)
  },
  updated(el, binding) {
    el._tooltipInstance?.update(binding)
  },
  unmounted(el) {
    if (el._tooltipInstance) {
      const idx = instances.indexOf(el._tooltipInstance)
      if (idx !== -1) instances.splice(idx, 1)
      el._tooltipInstance.destroy()
      delete el._tooltipInstance
    }
  }
}
